import React, { useState, useEffect, useCallback } from 'react';
import { Drawer, IconButton, Typography, Divider, Box, CircularProgress, Button } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ChatIcon from '@mui/icons-material/Chat';
import { FormattedMessage, injectIntl } from 'react-intl';
import Api from '../../../services/api';
import RequesterHistoryList from './requester-history-list';
import RequesterHistoryDetailsModal from './requester-history-details-modal';
import { makeStyles } from '@mui/styles';
import { getRequestingUserHistory } from '../../../services/requestinguserhistory.service';
import { PapiroConsole } from 'src/utils/papiroConsole';

// Largura ideal para o drawer - valor ajustado para melhor visualização da timeline
const drawerWidth = 580;

// Detecta o tema atual baseado nas variáveis CSS
const detectTheme = () => {
  const primaryColor = getComputedStyle(document.documentElement)
    .getPropertyValue('--mexx-green-primary')
    .trim();

  const isBlueTheme = primaryColor.includes('2979ff') || primaryColor.includes('#2979ff');
  return isBlueTheme ? 'blue' : 'green';
};

// Cores do header baseadas no tema
const getHeaderColors = (theme) => {
  if (theme === 'blue') {
    return {
      background: 'linear-gradient(135deg, #1E88E5 0%, #1565C0 100%)',
      hoverBg: 'rgba(255, 255, 255, 0.12)',
    };
  } else {
    return {
      background: 'linear-gradient(135deg, #7CB342 0%, #558B2F 100%)',
      hoverBg: 'rgba(255, 255, 255, 0.12)',
    };
  }
};

const useStyles = makeStyles((theme) => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0,
    [theme.breakpoints.down('lg')]: {
      width: 520,
    },
    [theme.breakpoints.down('md')]: {
      width: '85vw',
    },
    [theme.breakpoints.down('sm')]: {
      width: '95vw',
    },
  },
  drawerPaper: {
    width: drawerWidth,
    boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
    [theme.breakpoints.down('lg')]: {
      width: 520,
    },
    [theme.breakpoints.down('md')]: {
      width: '85vw',
    },
    [theme.breakpoints.down('sm')]: {
      width: '95vw',
    },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2.5, 3),
    justifyContent: 'space-between',
    color: theme.palette.common.white,
    minHeight: '68px',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
      minHeight: '60px',
    },
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flex: 1,
    minWidth: 0,
  },
  headerIcon: {
    opacity: 0.9,
  },
  content: {
    flexGrow: 1,
    padding: theme.spacing(0),
    overflowY: 'auto',
    height: 'calc(100vh - 84px)',
    backgroundColor: '#FAFAFA',
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100vh - 76px)',
    },
    // Scrollbar refinada
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#CFD8DC',
      borderRadius: '3px',
      '&:hover': {
        backgroundColor: '#B0BEC5',
      },
    },
  },
  detailsContent: {
    flexGrow: 1,
    padding: theme.spacing(0),
    overflow: 'hidden',
    height: 'calc(100vh - 84px)',
    backgroundColor: '#FAFAFA',
    [theme.breakpoints.down('sm')]: {
      height: 'calc(100vh - 76px)',
    },
  },
  emptyState: {
    padding: theme.spacing(8, 4),
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(4, 2),
      minHeight: '300px',
    },
  },
  emptyIcon: {
    fontSize: 64,
    color: '#CFD8DC',
    marginBottom: theme.spacing(2),
  },
  loadMoreContainer: {
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(3, 2, 4, 2),
  },
  loadMoreButton: {
    fontWeight: 600,
    letterSpacing: '0.5px',
    padding: theme.spacing(1.25, 4),
    borderRadius: '8px',
    textTransform: 'uppercase',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    transition: 'all 0.2s ease',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  },
  title: {
    fontWeight: 600,
    fontSize: '1.1rem',
    letterSpacing: '0.01em',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
    },
  },
  detailsTitle: {
    fontWeight: 600,
    fontSize: '0.95rem',
    letterSpacing: '0.01em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    [theme.breakpoints.down('sm')]: {
      fontSize: '0.85rem',
    },
  },
  subtitle: {
    fontSize: '0.8rem',
    opacity: 0.85,
    fontWeight: 400,
    marginTop: '2px',
  },
  closeButton: {
    color: 'white',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateX(2px)',
    },
  },
  backButton: {
    color: 'white',
    transition: 'all 0.2s ease',
    '&:hover': {
      transform: 'translateX(-2px)',
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
    gap: theme.spacing(2),
  },
}));

function RequesterHistorySidePanel({
  open,
  onClose,
  requestingUserId,
  requestingUserName,
  intl,
  setMessage,
  // Props para customização de URLs
  baseUrlCalled,
  baseUrlChat,
  getCustomUrl,
}) {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [currentTheme, setCurrentTheme] = useState('green');

  // Detecta tema atual
  useEffect(() => {
    const detected = detectTheme();
    setCurrentTheme(detected);

    const observer = new MutationObserver(() => {
      const newTheme = detectTheme();
      if (newTheme !== currentTheme) {
        setCurrentTheme(newTheme);
      }
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style']
    });

    return () => observer.disconnect();
  }, [currentTheme]);

  const headerColors = getHeaderColors(currentTheme);

  PapiroConsole.log("requestingUserId barra lateral")
  PapiroConsole.log(requestingUserId)

  const loadHistory = async (reset = false) => {
    if (!requestingUserId) return;

    setLoading(true);

    try {
      const currentPage = reset ? 1 : page + 1;

      const result = await getRequestingUserHistory(requestingUserId, currentPage, 10);

      PapiroConsole.log("result aqui")
      PapiroConsole.log(result)

      if (result && result.success) {
        PapiroConsole.log("result aqui 2")
        const responseData = result.data || result;
        const newItems = responseData.items || responseData.data?.items || [];

        const formattedItems = newItems.map(item => ({
          ...item,
          type: item.type || (item.calledId ? 'called' : 'chat'),
          id: item.id || item.calledId || item.chatId,
          createdat: item.createdat,
          title: item.title || item.name,
          status: item.status || item.statusname
        }));

        if (reset) {
          setHistory(formattedItems);
        } else {
          setHistory(prev => [...prev, ...formattedItems]);
        }

        const totalItems = responseData.totalitems || responseData.data?.totalitems || 0;
        const totalPages = responseData.totalpages || responseData.data?.totalpages || 0;

        PapiroConsole.log("currentPage")
        PapiroConsole.log(currentPage)
        PapiroConsole.log("totalPages")
        PapiroConsole.log(totalPages)
        setHasMore(currentPage < totalPages);
        setPage(currentPage);
      } else if (result && !result.response) {
        PapiroConsole.log("entrei aqui 1234")
        const responseData = result;
        const newItems = responseData.items || [];

        const formattedItems = newItems.map(item => ({
          ...item,
          type: item.type || (item.calledId ? 'called' : 'chat'),
          id: item.id || item.calledId || item.chatId,
          createdat: item.createdat || item.createdat || item.date,
          title: item.title || item.name,
          status: item.status || item.statusName
        }));

        if (reset) {
          setHistory(formattedItems);
        } else {
          setHistory(prev => [...prev, ...formattedItems]);
        }

        const totalItems = responseData.totalItems || 0;
        const totalPages = responseData.totalPages || 0;
        setHasMore(currentPage < totalPages);
        setPage(currentPage);
      } else {
        PapiroConsole.log("entrei aqui 12345678")
        const errorMessage = result?.response?.data?.errors
          ? result.response.data.errors[0]
          : intl.formatMessage({ id: "process.error" });

        console.error('Error loading history:', errorMessage);

        if (setMessage) {
          setMessage(true, "error", errorMessage);
        }

        Api.kickoff(result);
      }
    } catch (error) {
      console.error('Error loading history:', error);
      PapiroConsole.log("entrei aqui 123456789")

      if (setMessage) {
        const errorMessage = error?.response?.data?.errors
          ? error.response.data.errors[0]
          : intl.formatMessage({ id: "process.error" });
        setMessage(true, "error", errorMessage);
      }

      Api.kickoff(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && requestingUserId) {
      setHistory([]);
      setPage(0);
      setHasMore(false);
      setDetailsModalOpen(false);
      setSelectedItem(null);
      loadHistory(true);
    }
  }, [open, requestingUserId]);

  const handleLoadMore = () => {
    loadHistory(false);
  };

  const handleItemClick = (item) => {
    setSelectedItem(item);
    setDetailsModalOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsModalOpen(false);
    setSelectedItem(null);
  };

  /**
   * Função para gerar URL do item
   */
  const getItemUrl = useCallback((item) => {
    const isChat = item.type === 'chat' || item.type === 'Chat' || item.chatId;
    const itemId = item.id || item.calledId || item.chatId;

    if (getCustomUrl && typeof getCustomUrl === 'function') {
      return getCustomUrl(item);
    }

    if (isChat && baseUrlChat) {
      return `${baseUrlChat}/${itemId}`;
    }
    if (!isChat && baseUrlCalled) {
      return `${baseUrlCalled}/${itemId}`;
    }

    if (isChat) {
      return `/chat/${itemId}`;
    } else {
      return `/chamado/${itemId}`;
    }
  }, [getCustomUrl, baseUrlCalled, baseUrlChat]);

  const isItemChat = selectedItem && (selectedItem.type === 'chat' || selectedItem.type === 'Chat' || selectedItem.chatId);

  const getDetailHeaderTitle = () => {
    if (!selectedItem) return '';
    const itemId = selectedItem.id || selectedItem.calledId || selectedItem.chatId;
    if (isItemChat) {
      return `Conversa #${itemId}`;
    }
    return selectedItem.title
      ? `#${itemId} - ${selectedItem.title}`
      : `Chamado #${itemId}`;
  };

  return (
    <Drawer
      className={classes.drawer}
      variant="persistent"
      anchor="right"
      open={open}
      classes={{
        paper: classes.drawerPaper,
      }}
    >
      {/* Header - muda conforme o estado (lista ou detalhes) */}
      {detailsModalOpen && selectedItem ? (
        <Box
          className={classes.drawerHeader}
          sx={{
            background: headerColors.background,
          }}
        >
          <Box className={classes.headerContent}>
            <IconButton
              onClick={handleCloseDetails}
              className={classes.backButton}
              sx={{
                '&:hover': {
                  backgroundColor: headerColors.hoverBg,
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            {isItemChat ? (
              <ChatIcon className={classes.headerIcon} sx={{ fontSize: 22 }} color="inherit" />
            ) : (
              <AssignmentIcon className={classes.headerIcon} sx={{ fontSize: 22 }} color="inherit" />
            )}
            <Typography variant="h6" className={classes.detailsTitle}>
              {getDetailHeaderTitle()}
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            className={classes.closeButton}
            sx={{
              '&:hover': {
                backgroundColor: headerColors.hoverBg,
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      ) : (
        <Box
          className={classes.drawerHeader}
          sx={{
            background: headerColors.background,
          }}
        >
          <Box className={classes.headerContent}>
              <IconButton
            
              className={classes.backButton}
                sx={{ pointerEvents: 'none' }}
            >
              <HistoryIcon />
            </IconButton>
            <Box>
            <Typography variant="h6" className={classes.title}>
            {requestingUserName && requestingUserName.length > 0 ? (
              <FormattedMessage
                id="requester.history.title"
                defaultMessage="Histórico de Atendimentos - {requestingusername}"
                values={{ requestingusername: requestingUserName }}
              />
            ) : (
              <FormattedMessage
                id="requester.history.title"
                defaultMessage="Histórico de Atendimentos"
              />
            )}
          </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={onClose}
            className={classes.closeButton}
            sx={{
              '&:hover': {
                backgroundColor: headerColors.hoverBg,
              }
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}

      <Divider />

      {/* Conteúdo - alterna entre lista e detalhes */}
      {detailsModalOpen && selectedItem ? (
        <div className={classes.detailsContent}>
          <RequesterHistoryDetailsModal
            item={selectedItem}
            intl={intl}
            setMessage={setMessage}
            onClose={handleCloseDetails}
          />
        </div>
      ) : (
        <div className={classes.content}>
          {loading && history.length === 0 ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress color="primary" size={40} />
              <Typography variant="body2" color="textSecondary">
                Carregando histórico...
              </Typography>
            </Box>
          ) : history.length === 0 ? (
            <div className={classes.emptyState}>
              <HistoryIcon className={classes.emptyIcon} />
              <Typography
                variant="body1"
                color="textSecondary"
                sx={{
                  fontSize: '0.95rem',
                  maxWidth: '280px',
                  lineHeight: 1.6,
                }}
              >
                <FormattedMessage
                  id="requester.history.empty"
                  defaultMessage="Não há histórico de atendimentos para este solicitante."
                />
              </Typography>
            </div>
          ) : (
            <>
              <RequesterHistoryList
                items={history}
                onItemClick={handleItemClick}

              />

              {/* Botão CARREGAR MAIS 10 */}
              {hasMore && (
                <Box className={classes.loadMoreContainer}>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleLoadMore}
                    disabled={loading}
                    className={classes.loadMoreButton}
                  >
                    {loading && (
                      <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                    )}
                    CARREGAR MAIS 10
                  </Button>
                </Box>
              )}
            </>
          )}
        </div>
      )}
    </Drawer>
  );
}

export default injectIntl(RequesterHistorySidePanel);
